import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const DISMISS_KEY = "talkani-pwa-dismissed";
const DISMISS_TTL_DAYS = 14;

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function PWAInstallBanner() {
  const { t } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (isDismissed()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // delay to avoid being too aggressive
      setTimeout(() => setVisible(true), 15000);
    };

    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS fallback: show install hint after 25 seconds (no native prompt available)
    if (isIOS() && !isStandalone()) {
      const tt = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 25000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onPrompt);
        clearTimeout(tt);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted" || choice?.outcome === "dismissed") {
      setVisible(false);
      setDeferred(null);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-5 inset-x-5 md:bottom-5 md:start-auto md:end-5 md:inset-x-auto md:max-w-sm z-50 bg-[#0A0B0D] text-white shadow-2xl border border-neutral-800 rounded-sm p-5 animate-[reveal_.5s_ease-out]"
      data-testid="pwa-install-banner"
      role="dialog"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Close"
        className="absolute top-3 end-3 text-neutral-400 hover:text-white transition-colors"
        data-testid="pwa-install-close"
      >
        <X size={16} />
      </button>

      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 w-10 h-10 bg-[#FF5A00] flex items-center justify-center rounded-sm">
          <Smartphone size={20} />
        </div>
        <div className="flex-1 min-w-0 pe-6">
          <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-1">{t.pwa.label}</div>
          <h3 className="text-base font-bold tracking-tight leading-tight">{t.pwa.title}</h3>
        </div>
      </div>

      <p className="text-sm text-neutral-300 leading-relaxed mb-4">{t.pwa.body}</p>

      {iosHint ? (
        <p className="text-xs text-neutral-400 leading-relaxed bg-neutral-900 border border-neutral-800 px-3 py-2.5 rounded-sm">{t.pwa.ios_hint}</p>
      ) : (
        <button
          type="button"
          onClick={install}
          className="w-full btn-primary justify-center"
          data-testid="pwa-install-btn"
        >
          {t.pwa.install_btn} <Download size={16} />
        </button>
      )}
    </div>
  );
}
